import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { DueDateBadge } from '@/components/DueDateBadge';
import { getDueDateUrgency, type UrgencyLevel } from '@/utils/dueDateUrgency';
import { AlertTriangle } from 'lucide-react';
import type { BillableItem } from '@/lib/schemas';
import type { Permit } from '@/types/permits';

/** A single item that is pending acceptance and overdue (> 48h with no accepted_at). */
export interface OverdueAcceptanceItem {
  id: string;
  name: string;
  itemType: 'client_action' | 'deliverable';
  assignedTo: string | null;
  createdAt: string; // ISO datetime
}

interface ScopeWithAction {
  id: string;
  organization_name: string;
  scope_type_name: string;
  status: string;
  status_notes: string | null;
}

interface ActionSummaryWidgetProps {
  waitingOnUsCount: number;
  waitingOnThemCount: number;
  scopesWithActionNeeded: ScopeWithAction[];
  billableItems?: BillableItem[]; // Optional: billable items for due date tracking
  permits?: Permit[]; // Phase 7 Batch 6: permits for due date tracking
  overdueAcceptanceItems?: OverdueAcceptanceItem[]; // Phase 10e: items pending acceptance > 48h
  className?: string;
  // MED-04: Click handlers for navigating to specific items
  onScopeClick?: (scopeId: string) => void;
  onBillableItemClick?: (itemId: string) => void;
  onPermitClick?: (permitId: string) => void;
  // F6: clicking the count badges navigates to the relevant section.
  onWaitingOnUsClick?: () => void;
  onWaitingOnThemClick?: () => void;
}

/**
 * Displays action summary for a project (ball in court tracking + due dates).
 *
 * Shows:
 * - Count of scopes waiting on us (action required)
 * - Count of scopes waiting on them
 * - List of scopes requiring our action with details
 * - Billable items with upcoming due dates (Migration V011)
 * - Permits with upcoming due dates (Phase 7 Batch 6)
 */
export function ActionSummaryWidget({
  waitingOnUsCount,
  waitingOnThemCount,
  scopesWithActionNeeded,
  billableItems = [],
  permits = [],
  overdueAcceptanceItems = [],
  className,
  onScopeClick,
  onBillableItemClick,
  onPermitClick,
  onWaitingOnUsClick,
  onWaitingOnThemClick,
}: ActionSummaryWidgetProps) {
  // Render the action items list if either the explicit count says we have items
  // or the provided list contains items. This avoids hiding actionable scopes
  // in cases where the count isn't in sync with the list.
  const hasActionItems = waitingOnUsCount > 0 || scopesWithActionNeeded.length > 0;

  // Filter billable items with due dates that need attention (overdue, critical, warning)
  const itemsWithDueDates = billableItems
    .filter(item => !item.cancelled && item.due_date)
    .map(item => ({
      ...item,
      urgency: getDueDateUrgency(item.due_date),
    }))
    .filter(item => item.urgency && ['overdue', 'critical', 'warning'].includes(item.urgency.level))
    .sort((a, b) => (a.urgency?.daysUntilDue || 0) - (b.urgency?.daysUntilDue || 0)); // Sort by most urgent first

  const hasDueDateItems = itemsWithDueDates.length > 0;

  // Phase 7 Batch 6: Filter permits with due dates that need attention
  const permitsWithDueDates = permits
    .filter(permit => permit.due_date && permit.status !== 'closed')
    .map(permit => ({
      ...permit,
      urgency: getDueDateUrgency(permit.due_date!),
    }))
    .filter(permit => permit.urgency && ['overdue', 'critical', 'warning'].includes(permit.urgency.level))
    .sort((a, b) => (a.urgency?.daysUntilDue || 0) - (b.urgency?.daysUntilDue || 0)); // Sort by most urgent first

  const hasPermitDueDates = permitsWithDueDates.length > 0;
  const hasOverdueAcceptance = overdueAcceptanceItems.length > 0;

  return (
    <Card className={className} data-testid="action-summary-widget">
      <CardHeader>
        <CardTitle>Action Summary</CardTitle>
      </CardHeader>
      <CardContent>
        {/* Summary Counts — clickable when handlers are provided (F6) */}
        <div className="flex gap-4 mb-4 flex-wrap">
          <button
            type="button"
            onClick={onWaitingOnUsClick}
            disabled={!onWaitingOnUsClick || waitingOnUsCount === 0}
            className={`flex items-center gap-2 rounded-md px-2 py-1 transition-colors ${
              onWaitingOnUsClick && waitingOnUsCount > 0
                ? 'cursor-pointer hover:bg-muted'
                : 'cursor-default'
            }`}
            data-testid="action-summary-waiting-on-us"
            aria-label={`${waitingOnUsCount} items waiting on us`}
          >
            <Badge variant={waitingOnUsCount > 0 ? 'destructive' : 'secondary'}>
              {waitingOnUsCount}
            </Badge>
            <span className="text-sm font-medium">Waiting on Us</span>
          </button>
          <button
            type="button"
            onClick={onWaitingOnThemClick}
            disabled={!onWaitingOnThemClick || waitingOnThemCount === 0}
            className={`flex items-center gap-2 rounded-md px-2 py-1 transition-colors ${
              onWaitingOnThemClick && waitingOnThemCount > 0
                ? 'cursor-pointer hover:bg-muted'
                : 'cursor-default'
            }`}
            data-testid="action-summary-waiting-on-them"
            aria-label={`${waitingOnThemCount} items waiting on the customer`}
          >
            <Badge variant="secondary">{waitingOnThemCount}</Badge>
            <span className="text-sm font-medium">Waiting on Them</span>
          </button>
        </div>

        {/* Scope Action Items */}
        {hasActionItems && (
          <div className="space-y-3 mb-6">
            <h4 className="text-sm font-semibold text-gray-700">
              Scopes Requiring Our Action:
            </h4>
            <ul className="space-y-2">
              {scopesWithActionNeeded.map((scope) => (
                <li
                  key={scope.id}
                  data-testid={`action-scope-${scope.id}`}
                  className={`border-l-2 border-red-500 pl-3 py-1 rounded-r-md transition-colors ${
                    onScopeClick ? 'cursor-pointer hover:bg-gray-50' : ''
                  }`}
                  onClick={onScopeClick ? () => onScopeClick(scope.id) : undefined}
                  role={onScopeClick ? 'button' : undefined}
                  tabIndex={onScopeClick ? 0 : undefined}
                  onKeyDown={onScopeClick ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onScopeClick(scope.id);
                    }
                  } : undefined}
                >
                  <div className="flex items-baseline gap-2">
                    <span className="font-medium text-sm">
                      {scope.organization_name}
                    </span>
                    <span className="text-xs text-gray-500">
                      ({scope.scope_type_name})
                    </span>
                  </div>
                  {scope.status_notes && (
                    <p className="text-sm text-gray-600 mt-1">
                      {scope.status_notes}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Due Date Items (Migration V011) */}
        {hasDueDateItems && (
          <div className="space-y-3">
            <h4 className="text-sm font-semibold text-gray-700">
              Items with Upcoming Due Dates:
            </h4>
            <ul className="space-y-2">
              {itemsWithDueDates.map((item) => (
                <li
                  key={item.id}
                  data-testid={`action-item-${item.id}`}
                  className={`border-l-2 pl-3 py-1 rounded-r-md transition-colors ${
                    item.urgency?.level === 'overdue'
                      ? 'border-red-500'
                      : item.urgency?.level === 'critical'
                      ? 'border-orange-500'
                      : 'border-yellow-500'
                  } ${onBillableItemClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={onBillableItemClick ? () => onBillableItemClick(item.id) : undefined}
                  role={onBillableItemClick ? 'button' : undefined}
                  tabIndex={onBillableItemClick ? 0 : undefined}
                  onKeyDown={onBillableItemClick ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onBillableItemClick(item.id);
                    }
                  } : undefined}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">
                      {item.description}
                    </span>
                    <DueDateBadge dueDate={item.due_date} />
                  </div>
                  {item.status_notes && (
                    <p className="text-sm text-gray-600 mt-1">
                      {item.status_notes}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Phase 7 Batch 6: Permit Due Dates */}
        {hasPermitDueDates && (
          <div className={`space-y-3 ${hasDueDateItems ? 'mt-6' : ''}`}>
            <h4 className="text-sm font-semibold text-gray-700">
              Permits with Upcoming Due Dates:
            </h4>
            <ul className="space-y-2">
              {permitsWithDueDates.map((permit) => (
                <li
                  key={permit.id}
                  data-testid={`action-permit-${permit.id}`}
                  className={`border-l-2 pl-3 py-1 rounded-r-md transition-colors ${
                    permit.urgency?.level === 'overdue'
                      ? 'border-red-500'
                      : permit.urgency?.level === 'critical'
                      ? 'border-orange-500'
                      : 'border-yellow-500'
                  } ${onPermitClick ? 'cursor-pointer hover:bg-gray-50' : ''}`}
                  onClick={onPermitClick ? () => onPermitClick(permit.id) : undefined}
                  role={onPermitClick ? 'button' : undefined}
                  tabIndex={onPermitClick ? 0 : undefined}
                  onKeyDown={onPermitClick ? (e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onPermitClick(permit.id);
                    }
                  } : undefined}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">
                      {permit.permit_type}
                    </span>
                    <DueDateBadge dueDate={permit.due_date!} />
                  </div>
                  {permit.notes && (
                    <p className="text-sm text-gray-600 mt-1">
                      {permit.notes}
                    </p>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Phase 10e: Acceptance overdue items */}
        {hasOverdueAcceptance && (
          <div className={`space-y-3 ${hasDueDateItems || hasPermitDueDates ? 'mt-6' : ''}`}>
            <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-1.5">
              <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
              Awaiting Acceptance (Overdue):
            </h4>
            <ul className="space-y-2">
              {overdueAcceptanceItems.map((item) => {
                const ageMs = Date.now() - new Date(item.createdAt).getTime();
                const ageDays = Math.floor(ageMs / 86_400_000);
                const ageHours = Math.floor(ageMs / 3_600_000);
                const ageLabel = ageDays > 0 ? `Sent ${ageDays} day${ageDays !== 1 ? 's' : ''} ago` : `Sent ${ageHours}h ago`;
                const typeLabel = item.itemType === 'client_action' ? 'Action Item' : 'Deliverable';
                return (
                  <li
                    key={`${item.itemType}-${item.id}`}
                    data-testid={`acceptance-overdue-${item.itemType}-${item.id}`}
                    className="border-l-2 border-amber-400 pl-3 py-1 rounded-r-md bg-amber-50/40"
                  >
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <span className="font-medium text-sm text-gray-900 truncate">{item.name}</span>
                      <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 whitespace-nowrap">
                        {typeLabel}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-0.5 text-xs text-muted-foreground flex-wrap">
                      {item.assignedTo && <span>{item.assignedTo}</span>}
                      <span className="text-amber-600 font-medium">{ageLabel}</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* No Action Items */}
        {!hasActionItems && !hasDueDateItems && !hasPermitDueDates && !hasOverdueAcceptance && (
          <p className="text-sm text-gray-500">
            No actions required. All items are on track.
          </p>
        )}
      </CardContent>
    </Card>
  );
}
